import { useState, useEffect } from 'react';
import { Icon } from './Icons';

export default function ContactModal({ onClose }) {
  const [from, setFrom] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!from || !subject || !message || status === 'sending') return;
    setStatus('sending');
    try {
      const res = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from, subject, message }),
      });
      setStatus(res.ok ? 'sent' : 'error');
    } catch {
      setStatus('error');
    }
  };

  const field = {
    flex: 1, border: 'none', outline: 'none',
    fontFamily: "'Nunito', sans-serif", fontSize: 14,
    color: 'var(--text)', background: 'transparent',
  };
  const row = {
    display: 'flex', alignItems: 'center',
    borderBottom: '1px solid var(--border)', padding: '10px 18px', gap: 10,
  };
  const label = {
    fontFamily: "'Courier Prime', monospace", fontSize: 11,
    color: 'var(--text3)', letterSpacing: '.08em', minWidth: 52,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(42,31,45,.52)',
        WebkitBackdropFilter: 'blur(5px)', backdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'fadeIn .18s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 520, maxWidth: 'calc(100vw - 32px)',
          background: 'white', borderRadius: 18,
          boxShadow: '0 24px 80px rgba(42,31,45,.3)',
          overflow: 'hidden',
          animation: 'slideUp .22s ease',
        }}
      >
        {/* Title bar */}
        <div style={{
          background: 'linear-gradient(135deg, var(--ink), var(--plum))',
          padding: '13px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="email" size={15} stroke="var(--rose)" />
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 700, fontStyle: 'italic', color: 'white' }}>
              New Message
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,.55)', fontSize: 20, lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>

        {status === 'sent' ? (
          <div style={{ padding: '52px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>✉️</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 900, fontStyle: 'italic', color: 'var(--ink)', marginBottom: 8 }}>
              Message sent!
            </div>
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 28, lineHeight: 1.6 }}>
              Makayla will get back to you soon.
            </p>
            <button onClick={onClose} style={{ padding: '9px 26px', borderRadius: 11, border: 'none', background: 'var(--crimson)', color: 'white', fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* To */}
            <div style={row}>
              <span style={label}>To</span>
              <span style={{
                background: 'linear-gradient(135deg, var(--blush), var(--lavender))',
                border: '1.5px solid var(--border2)', borderRadius: 20,
                padding: '3px 12px', fontSize: 13, fontWeight: 700,
                color: 'var(--crimson)', fontFamily: "'Nunito', sans-serif",
              }}>
                Makayla Richards
              </span>
            </div>
            {/* From */}
            <div style={row}>
              <span style={label}>From</span>
              <input
                type="email" value={from} required
                onChange={e => setFrom(e.target.value)}
                placeholder="your@email.com"
                style={field}
              />
            </div>
            {/* Subject */}
            <div style={row}>
              <span style={label}>Subject</span>
              <input
                type="text" value={subject} required
                onChange={e => setSubject(e.target.value)}
                placeholder="What's on your mind?"
                style={field}
              />
            </div>
            {/* Body */}
            <textarea
              value={message} required rows={7}
              onChange={e => setMessage(e.target.value)}
              placeholder="Write your message here…"
              style={{ ...field, width: '100%', resize: 'none', padding: '14px 18px', display: 'block', lineHeight: 1.6 }}
            />
            {/* Footer */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 18px', borderTop: '1px solid var(--border)', background: 'var(--warm)',
            }}>
              {status === 'error'
                ? <span style={{ fontSize: 12, color: 'var(--crimson)' }}>Something went wrong, try mrichards.assistant@gmail.com directly.</span>
                : <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'Courier Prime', monospace" }}>// replies go to your address</span>
              }
              <button
                type="submit"
                disabled={status === 'sending'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '9px 20px', borderRadius: 11, border: 'none',
                  background: 'var(--crimson)', color: 'white',
                  fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 800,
                  cursor: status === 'sending' ? 'wait' : 'pointer',
                }}
              >
                <Icon name="email" size={13} stroke="white" />
                {status === 'sending' ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
